import React, { useEffect, useState } from 'react';
import { History, Loader } from 'lucide-react';
import api from '../../services/api';
import { formatDateTime } from '../../utils/formatDate';

// Reusable across Lead/Deal/Account/Contact/Task detail pages — renders the
// AuditLog trail for a single record (src/models/AuditLog.js, written by
// AuditLogService.log() on every create/update/delete/share/transfer).
// Read-only; there is nothing to edit here, just history.
const ACTION_LABELS = {
  create: 'Created',
  update: 'Updated',
  delete: 'Deleted',
  share: 'Shared',
  unshare: 'Unshared',
};

function actionLabel(action) {
  const verb = action?.split('.')[1];
  return ACTION_LABELS[verb] || verb || action;
}

// Defensive fallback for any diff value that's still an object by the time
// it reaches the frontend (the backend now normalizes populated refs down
// to a plain id before storing a diff — see src/utils/auditDiff.js — but
// this covers any resource type that hasn't been migrated onto that helper
// yet) — pulls out whichever human-readable field exists instead of
// dumping raw JSON into the history feed.
function formatValue(v) {
  if (v === null || v === undefined || v === '') return '—';
  if (typeof v === 'object' && !Array.isArray(v)) {
    return v.name || v.title || v.email || v.phoneNumber || (v._id ? String(v._id) : JSON.stringify(v));
  }
  if (Array.isArray(v)) return v.length ? `${v.length} item(s)` : '—';
  return String(v);
}

export default function AuditHistory({ resourceType, resourceId }) {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!resourceId) return;
    setLoading(true);
    api.get('/crm/audit-logs', { params: { resourceType, resourceId, limit: 100 } })
      .then(res => setLogs(res.data.logs || []))
      .catch(() => setLogs([]))
      .finally(() => setLoading(false));
  }, [resourceType, resourceId]);

  if (loading) {
    return (
      <div className="py-16 flex flex-col items-center gap-3 text-[#706E6B]">
        <Loader size={24} className="animate-spin" />
        <p className="text-[11px] font-bold uppercase tracking-widest">Loading history…</p>
      </div>
    );
  }

  if (logs.length === 0) {
    return (
      <div className="py-24 text-center border-2 border-dashed border-[#D8DDE6] rounded h-full bg-white opacity-40">
        <History size={48} className="mx-auto mb-4 opacity-10" />
        <p className="text-[11px] font-bold text-[#706E6B] uppercase tracking-widest">No history recorded yet</p>
      </div>
    );
  }

  return (
    <div className="space-y-4 animate-in slide-in-from-bottom-4">
      {logs.map(log => (
        <div key={log._id} className="sf-card p-4 flex gap-4 hover:border-[#0176D3] transition-all">
          <div className="w-10 h-10 rounded border border-[#D8DDE6] bg-[#F4F6F9] flex items-center justify-center shrink-0">
            <History size={18} className="text-[#0176D3]" />
          </div>
          <div className="flex-1">
            <div className="flex items-center justify-between mb-1">
              <h4 className="text-[14px] font-bold text-[#080707]">
                {actionLabel(log.action)} by <span className="text-[#0176D3]">{log.userName || 'System'}</span>
              </h4>
              <span className="text-[11px] font-bold text-[#706E6B]">{formatDateTime(log.createdAt)}</span>
            </div>
            {log.diff && Object.keys(log.diff).length > 0 ? (
              <div className="mt-2 space-y-1">
                {Object.entries(log.diff).map(([field, change]) => (
                  <div key={field} className="text-[12px] text-[#706E6B] flex items-center gap-2">
                    <span className="font-bold text-[#080707] uppercase text-[10px] tracking-wider">{field}</span>
                    <span className="line-through opacity-60">{formatValue(change?.before)}</span>
                    <span>→</span>
                    <span className="font-medium text-[#080707]">{formatValue(change?.after)}</span>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-[12px] text-[#706E6B]">No field changes recorded for this event.</p>
            )}
          </div>
        </div>
      ))}
    </div>
  );
}
